import React, { useState } from 'react';
import { styled } from '@mui/material/styles';
import Paper from '@mui/material/Paper';
import ToggleButton from '@mui/material/ToggleButton';
import ToggleButtonGroup from '@mui/material/ToggleButtonGroup';
import { ISortingDropdownOption } from 'shared/components/sortingDropdown';
import { colors } from 'styles/colors';

const StyledToggleButtonGroup = styled(ToggleButtonGroup)(({ theme }) => ({
  '& .MuiToggleButtonGroup-grouped': {
    border: 0,
    '&.Mui-disabled': {
      border: 0,
    },
    '&:not(:first-of-type)': {
      borderTopLeftRadius: '4rem',
      borderBottomLeftRadius: '4rem',
      textTransform: 'capitalize',
      paddingLeft: '1rem',
      paddingRight: '1rem',
    },
    '&:first-of-type': {
      borderTopRightRadius: '4rem',
      borderBottomRightRadius: '4rem',
      textTransform: 'capitalize',
      paddingLeft: '1rem',
      paddingRight: '1rem',
    },
  },
  '& .Mui-selected': {
    borderRadius: '4rem',
    backgroundColor: `${colors.tertiary.white} !important`,
  },
}));

interface IToggleButtonsProps<T extends unknown> {
  data: T[];
  setData: (data: T[]) => void;
  options: ISortingDropdownOption<T>[];
  defaultSelectedOption?: number;
}

const SortingToggle = <T extends unknown>({
  data,
  setData,
  options,
  defaultSelectedOption,
}: IToggleButtonsProps<T>) => {
  const [toggleValue, setToggleValue] = useState<number>(
    defaultSelectedOption || 0
  );

  const handleToggle = (
    event: React.MouseEvent<HTMLElement>,
    option: number
  ) => {
    const sortedData = data?.sort(options[option]?.sortFunction);
    setToggleValue(option);
    setData([...sortedData]);
  };

  return (
    <Paper
      elevation={0}
      sx={{
        display: 'flex',
        border: (theme) => `2px solid ${colors.background.midGray}`,
        borderRadius: ({ typography }) => typography.pxToRem(64),
        flexWrap: 'wrap',
        width: 'fit-content',
        backgroundColor: colors.background.midGray,
      }}
      data-testid='sortingToggle-paper'
    >
      <StyledToggleButtonGroup
        size='small'
        value={toggleValue}
        exclusive
        onChange={handleToggle}
        aria-label='toggle button group'
        data-testid='sortingToggle-group'
      >
        {options?.map((option, index) => {
          return (
            <ToggleButton
              key={index}
              value={index}
              aria-label={option.label}
              className='kfic2-bodyTextSmall'
              data-testid={`sortingToggle-${option.value}`}
            >
              {option.label}
            </ToggleButton>
          );
        })}
      </StyledToggleButtonGroup>
    </Paper>
  );
};

export default SortingToggle;
